import { listGames, applyCatalogRefresh } from "./games.js";
import {
	listTvSeries,
	getCatalog as getTvCatalog,
	saveCatalog as saveTvCatalog,
} from "./tv.js";
import {
	listAnime,
	getCatalog as getAnimeCatalog,
	saveCatalog as saveAnimeCatalog,
} from "./anime.js";
import { listMovies, applyCatalogRefresh as applyMovieRefresh } from "./movies.js";
import { getGame } from "../api/igdb.js";
import { getTvEpisodes } from "../api/tv.js";
import { getAnimeEpisodes } from "../api/anime.js";
import { getMovie } from "../api/movies.js";
import { todayDate, shiftDate } from "../utils/dates.js";
export { shiftDate } from "../utils/dates.js";
import { isCatalogStale, seasonPremiereDate } from "../utils/seasons.js";

// Age-based catalog refresh.
//
// Release dates move: games get postponed or go from "TBD" to a real date, TV and
// anime seasons shift their premiere or get announced. The app used to copy that
// data ONCE (at add time / first detail open) and never look again, so the Release
// schedule and the bell drifted away from reality. This module re-checks the
// sources on a schedule:
//
//   - Games: every tracked game that could still move (see gameNeedsRefresh) is
//     re-fetched from IGDB once a day; a changed date overwrites the stored one.
//   - TV/anime: a cached season/episode catalog is trusted for CATALOG_MAX_AGE_DAYS
//     (utils/seasons.js). The detail views re-fetch a stale one on open; the daily
//     pass below refreshes up to CATALOG_BATCH_LIMIT stale catalogs, most relevant
//     first, so upcoming seasons stay current even for series you don't open.
//
// Books are not refreshed: Open Library only gives a publish year and its
// upcoming-title data is too thin to be worth polling.

// A game released this long ago is still re-checked. Its stored date may have
// been postponed while the app sat closed; without this window it would look
// released and drop off the schedule for good.
export const GAME_RECENT_WINDOW_DAYS = 60;
// Max TV/anime catalogs re-fetched per daily pass (bounds TVmaze traffic; a large
// library rotates through over several days, most relevant first).
export const CATALOG_BATCH_LIMIT = 20;
// Max movies re-checked at OMDb per daily pass. OMDb's free key allows 1,000
// requests a day, shared with searches and adds; a 429 ends the movie loop for
// the day (design_documents/movies-category.md).
export const MOVIE_REFRESH_LIMIT = 50;
// A season that premiered within this window still counts as "in flux" for
// prioritising which stale catalogs to refresh first.
export const RECENT_SEASON_WINDOW_DAYS = 30;
// Per-game re-check throttle within a session (detail opens + the daily pass).
const RECHECK_MS = 24 * 60 * 60 * 1000;
// Persisted "YYYY-MM-DD" of the last completed daily pass (per device).
export const LAST_RUN_KEY = "catalog_refresh_last_run";

// ---- pure helpers (exported for tests) --------------------------------------

// shiftDate lives in utils/dates.js (re-exported above for existing callers).

// Could this game's release data still change at IGDB? True for a game with no
// precise date that is TBD or expected this year or later, and for any game whose
// date is in the future or within the last GAME_RECENT_WINDOW_DAYS. A dateless
// game from years ago is left alone here (the detail view still backfills it on
// open) — polling it daily would never pay off.
export function releaseNeedsRefresh(item, idKey, today) {
	if (!item?.[idKey] || !today) return false;
	if (item.release_date == null) {
		const year = Number(today.slice(0, 4));
		return item.release_year == null || item.release_year >= year;
	}
	return item.release_date >= shiftDate(today, -GAME_RECENT_WINDOW_DAYS);
}

export function gameNeedsRefresh(game, today) {
	return releaseNeedsRefresh(game, "igdb_id", today);
}

// Movies follow the same rule. OMDb's date is the US theatrical one and stays
// blank ("TBD") for far-off titles, so those are polled until it lands.
export function movieNeedsRefresh(movie, today) {
	return releaseNeedsRefresh(movie, "imdb_id", today);
}

// Which stale catalogs matter most (lower = sooner):
//   0 never fetched — its seasons can't feed Incoming until it exists;
//   1 has a season that is unaired, undated, or premiered in the last
//     RECENT_SEASON_WINDOW_DAYS — dates still in flux;
//   2 everything else (ended-looking shows; same weekly cadence, just last).
export function catalogPriority(catalog, today) {
	if (!catalog) return 0;
	const episodes = catalog.episodes ?? [];
	const cutoff = shiftDate(today, -RECENT_SEASON_WINDOW_DAYS);
	for (const s of catalog.seasons ?? []) {
		if (s?.number == null) continue;
		const d = seasonPremiereDate(s, episodes);
		if (d == null || d >= cutoff) return 1;
	}
	return 2;
}

// From [{ series, kind, catalog }] pick the ones whose catalog is missing or stale,
// ordered by priority then by age (oldest first; never-fetched first of all),
// capped to `limit`. Pure: the caller loads the catalogs.
export function pickStaleCatalogs(entries, { now, today, limit }) {
	const due = [];
	for (const entry of entries) {
		if (!entry?.series?.tvmaze_id) continue;
		const { catalog } = entry;
		if (catalog && !isCatalogStale(catalog, now)) continue;
		const fetched = catalog?.fetched_at ? Date.parse(catalog.fetched_at) : NaN;
		due.push({
			entry,
			priority: catalogPriority(catalog, today),
			age: Number.isNaN(fetched) ? -Infinity : fetched,
		});
	}
	due.sort((a, b) => a.priority - b.priority || a.age - b.age);
	return due.slice(0, limit ?? due.length).map((d) => d.entry);
}

// Cheap "did the catalog materially change" check between two fetches: season
// numbers/dates/episode counts and the episode list size. Used only to decide
// whether a screen showing release info is worth re-rendering.
export function catalogSignature(catalog) {
	if (!catalog) return "";
	const seasons = (catalog.seasons ?? [])
		.map((s) => `${s.number}:${s.premiere_date ?? ""}:${s.episode_count ?? ""}`)
		.join("|");
	return `${seasons}#${(catalog.episodes ?? []).length}`;
}

// ---- per-session throttle -----------------------------------------------------

const gameCheckedAt = new Map(); // igdb_id -> ms of last successful check

export function gameRecentlyChecked(igdbId, now = Date.now()) {
	const at = gameCheckedAt.get(igdbId);
	return at != null && now - at < RECHECK_MS;
}

function markGameChecked(igdbId, now = Date.now()) {
	gameCheckedAt.set(igdbId, now);
}

const movieCheckedAt = new Map(); // imdb_id -> ms of last successful check

export function movieRecentlyChecked(imdbId, now = Date.now()) {
	const at = movieCheckedAt.get(imdbId);
	return at != null && now - at < RECHECK_MS;
}

function markMovieChecked(imdbId, now = Date.now()) {
	movieCheckedAt.set(imdbId, now);
}

// Test hook.
export function _resetSessionState() {
	gameCheckedAt.clear();
	movieCheckedAt.clear();
}

// ---- refreshers ---------------------------------------------------------------

// Re-fetch one tracked game from IGDB and apply any release-date (and missing
// franchise/collection) changes. Resolves to the updated record when something
// changed, else null. Rejects on network/worker errors (callers decide).
export async function refreshGame(game) {
	if (!game?.igdb_id) return null;
	const fresh = await getGame(game.igdb_id);
	markGameChecked(game.igdb_id);
	const result = await applyCatalogRefresh(game.id, fresh);
	return result?.changed ? result.record : null;
}

// Re-fetch one tracked movie from OMDb and apply any release-date change (plus
// metadata that was still missing). Resolves to the updated record when
// something changed, else null. Rejects on network/worker errors — a 429 means
// the day's OMDb quota is gone.
export async function refreshMovie(movie) {
	if (!movie?.imdb_id) return null;
	const fresh = await getMovie(movie.imdb_id);
	markMovieChecked(movie.imdb_id);
	const result = await applyMovieRefresh(movie.id, fresh);
	return result?.changed ? result.record : null;
}

// Re-fetch one series' season/episode catalog from TVmaze into the local cache
// (`kind` = "tv" | "anime"). Resolves { catalog, changed } or null when the worker
// had nothing. Rejects on network/worker errors.
export async function refreshSeriesCatalog(series, kind, previous = null) {
	if (!series?.tvmaze_id) return null;
	const fetchFn = kind === "anime" ? getAnimeEpisodes : getTvEpisodes;
	const save = kind === "anime" ? saveAnimeCatalog : saveTvCatalog;
	const data = await fetchFn(series.tvmaze_id);
	if (!data) return null;
	const catalog = await save(data);
	return {
		catalog,
		changed: catalogSignature(previous) !== catalogSignature(catalog),
	};
}

// True when today's pass hasn't completed yet on this device.
export function dailyRefreshDue(today = todayDate()) {
	try {
		return localStorage.getItem(LAST_RUN_KEY) !== today;
	} catch {
		return true;
	}
}

let running = null;

// The daily pass. Runs at most once per calendar day per device (`force` skips
// that check), only when online; concurrent callers share one run. Per-item
// failures are logged and skipped — a game IGDB no longer knows (404) is simply
// left as-is. Resolves { games, catalogs, failures } — counts of records that
// actually changed — or null when the pass didn't run.
export async function runDailyCatalogRefresh({ force = false } = {}) {
	const today = todayDate();
	if (!force && !dailyRefreshDue(today)) return null;
	if (typeof navigator !== "undefined" && navigator.onLine === false) return null;
	if (running) return running;
	running = (async () => {
		const result = { games: 0, movies: 0, catalogs: 0, failures: 0 };

		for (const game of await listGames()) {
			if (!gameNeedsRefresh(game, today)) continue;
			if (gameRecentlyChecked(game.igdb_id)) continue;
			try {
				if (await refreshGame(game)) result.games++;
			} catch (err) {
				if (err?.status === 404) continue;
				result.failures++;
				console.warn(
					`Release-date refresh failed for "${game.title}":`,
					err?.message ?? err,
				);
			}
		}

		// Movies: same rule, capped per pass to respect OMDb's daily quota; a 429
		// (quota gone) stops the loop for today rather than burning 50 failures.
		let movieChecks = 0;
		for (const movie of await listMovies()) {
			if (movieChecks >= MOVIE_REFRESH_LIMIT) break;
			if (!movieNeedsRefresh(movie, today)) continue;
			if (movieRecentlyChecked(movie.imdb_id)) continue;
			movieChecks++;
			try {
				if (await refreshMovie(movie)) result.movies++;
			} catch (err) {
				if (err?.status === 404) continue;
				result.failures++;
				console.warn(
					`Release-date refresh failed for "${movie.title}":`,
					err?.message ?? err,
				);
				if (err?.status === 429) break;
			}
		}

		const [tv, anime] = await Promise.all([listTvSeries(), listAnime()]);
		const entries = [];
		for (const series of tv) {
			if (!series.tvmaze_id) continue;
			entries.push({
				series,
				kind: "tv",
				catalog: await getTvCatalog(series.tvmaze_id),
			});
		}
		for (const series of anime) {
			if (!series.tvmaze_id) continue;
			entries.push({
				series,
				kind: "anime",
				catalog: await getAnimeCatalog(series.tvmaze_id),
			});
		}
		const picked = pickStaleCatalogs(entries, {
			now: Date.now(),
			today,
			limit: CATALOG_BATCH_LIMIT,
		});
		for (const { series, kind, catalog } of picked) {
			try {
				const r = await refreshSeriesCatalog(series, kind, catalog);
				if (r?.changed) result.catalogs++;
			} catch (err) {
				if (err?.status === 404) continue;
				result.failures++;
				console.warn(
					`Catalog refresh failed for "${series.title}":`,
					err?.message ?? err,
				);
			}
		}

		// The pass completed (even with some per-item failures): don't redo it
		// until tomorrow. A pass that never started (offline) leaves the key alone.
		try {
			localStorage.setItem(LAST_RUN_KEY, today);
		} catch {
			// storage unavailable — we'll simply run again next launch
		}
		return result;
	})().finally(() => {
		running = null;
	});
	return running;
}
